import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, setToken, getToken } from "../lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Fingerprint, Lock, ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function AdminLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (getToken()) {
      navigate("/admin/dashboard");
    }
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email || !password) {
      setError("Please enter your email and password.");
      return;
    }
    setLoading(true);
    try {
      const res = await api.post("/auth/login", { email, password });
      setToken(res.data.token);
      toast.success("Welcome back!");
      navigate("/admin/dashboard");
    } catch (err) {
      setError(err.response?.data?.detail || "Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[color:var(--brand-navy)] flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm mb-6 transition-colors duration-200"
          data-testid="admin-back-to-site-link"
        >
          <ArrowLeft className="h-4 w-4" /> Back to website
        </Link>
        <Card className="shadow-xl border-0">
          <CardHeader className="space-y-3 pb-4">
            <img src="/logo.png" alt="Cayman Fingerprinting Services Ltd." className="h-14 w-auto self-start" />
            <div>
              <CardTitle className="text-xl font-heading text-[color:var(--brand-navy)]">Admin Dashboard</CardTitle>
              <CardDescription className="text-xs mt-0.5">Staff sign in</CardDescription>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="admin-login-form">
              <div className="space-y-1.5">
                <Label htmlFor="admin-email">Email address</Label>
                <Input
                  id="admin-email"
                  type="email"
                  placeholder="admin@caymanfingerprinting.ky"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="admin-login-email-input"
                  autoComplete="username"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="admin-password">Password</Label>
                <Input
                  id="admin-password"
                  type="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="admin-login-password-input"
                  autoComplete="current-password"
                />
              </div>
              {error && (
                <p className="text-sm text-destructive" role="alert" aria-live="polite" data-testid="admin-login-error">
                  {error}
                </p>
              )}
              <Button
                type="submit"
                className="w-full h-11 bg-[color:var(--brand-navy)] hover:bg-[color:var(--brand-navy-2)] btn-lift"
                disabled={loading}
                data-testid="admin-login-submit-button"
              >
                {loading ? (
                  <span className="inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Signing in...</span>
                ) : (
                  "Sign In"
                )}
              </Button>
              <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                <Lock className="h-3 w-3" /> Authorized staff only. All access is logged.
              </p>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
